import React from 'react';
import Icon from '../AppIcon';
import ColumnFilterMenu from './ColumnFilterMenu';

/**
 * Table header cell: sorting and filtering in one control.
 *
 * Clicking the header used to toggle asc/desc directly. That is the cheapest
 * possible thing to put on a header and it answers the wrong question — in a
 * submissions table you far more often want "show me only the papers needing
 * revisions" than "order everything by status alphabetically".
 *
 * So the header now opens a menu that carries both: sort at the top, then a
 * checkbox list of the values actually present in that column, with counts.
 * Several columns can be filtered at once; within a column values are OR'd,
 * across columns they are AND'd. See `useTableFilters`.
 *
 * A column with no `filter` prop keeps the old behaviour and toggles sort on
 * click, so nothing that hasn't been migrated changes.
 *
 * Accessibility: `aria-sort` remains the real signal and doubles as the styling
 * hook (`.table-clear th[aria-sort]`), so visual and assistive state cannot
 * drift. The trigger is a real <button> inside the <th> rather than a click
 * handler on the cell — a <th> with onClick is invisible to keyboard users
 * unless you hand-roll the roles, which the previous version did by adding
 * tabIndex and a keydown handler to a non-interactive element.
 *
 *   const { sort, toggle, sortRows } = useSort('submittedAt');
 *   const f = useTableFilters(rows, columns);
 *   <SortableTh field="status" sort={sort} onSort={toggle} filter={f.headerProps('status')}>
 *     Status
 *   </SortableTh>
 */
export default function SortableTh({
  field,
  sort,               // { key, direction: 'asc' | 'desc' }
  onSort,             // (field) => void — toggles
  onSortTo,           // (field, 'asc'|'desc') => void — optional, preferred
  filter,             // from useTableFilters().headerProps(field) — optional
  children,
  align = 'start',    // 'start' | 'end' — numeric columns sort from the end
  className = '',
  ...rest
}) {
  const [open, setOpen] = React.useState(false);
  const btnRef = React.useRef(null);

  const active = sort?.key === field;
  const direction = active ? sort.direction : null;
  const ariaSort = !active ? 'none' : direction === 'asc' ? 'ascending' : 'descending';
  const filtered = (filter?.selected?.length || 0) > 0;

  // Without a filter config this stays a plain sort toggle.
  const handleClick = () => (filter ? setOpen((o) => !o) : onSort?.(field));

  /* The menu asks for an explicit direction; `onSort` is a toggle keyed on the
     field. `onSortTo` is used when a caller passes it. Otherwise this maps the
     request onto the toggle:
       - already the sorted column   -> one call flips it, if it needs flipping
       - not the sorted column       -> one call selects it ascending, and a
                                        second flips to descending
     Two calls in one tick are safe because `useSort.toggle` is a functional
     update, so the second sees the result of the first rather than stale state. */
  const sortTo = (f, dir) => {
    if (onSortTo) return onSortTo(f, dir);
    if (sort?.key === f) {
      if (sort.direction !== dir) onSort?.(f);
      return;
    }
    onSort?.(f);                 // -> ascending
    if (dir === 'desc') onSort?.(f);
  };

  return (
    <th
      aria-sort={ariaSort}
      scope="col"
      className={`${align === 'end' ? 'text-end' : ''} ${className}`.trim()}
      {...rest}
    >
      <button
        ref={btnRef}
        type="button"
        onClick={handleClick}
        aria-haspopup={filter ? 'dialog' : undefined}
        aria-expanded={filter ? open : undefined}
        // Inherit the header's own type — this button must be invisible as a
        // button and read purely as the column label.
        className={`inline-flex items-center gap-1 font-[inherit] text-[inherit] tracking-[inherit] uppercase focus-ring ${
          align === 'end' ? 'flex-row-reverse' : ''
        } ${filtered ? 'text-primary' : ''}`}
      >
        {children}
        {/* Reserve both glyphs' space always, so the header row doesn't change
            width when you sort or filter a different column. */}
        <Icon
          name={!active ? 'ChevronsUpDown' : direction === 'asc' ? 'ChevronUp' : 'ChevronDown'}
          size={12}
          className={active ? 'text-primary' : 'text-muted-foreground/50'}
        />
        {filter && (
          <Icon
            name="Filter"
            size={11}
            className={filtered ? 'text-primary' : 'text-muted-foreground/35'}
          />
        )}
      </button>

      {open && filter && (
        <ColumnFilterMenu
          anchorEl={btnRef.current}
          onClose={() => setOpen(false)}
          field={field}
          label={filter.label}
          options={filter.options}
          selected={filter.selected}
          onToggle={filter.onToggle}
          onOnly={filter.onOnly}
          onClear={filter.onClear}
          onSelectAll={filter.onSelectAll}
          sort={sort}
          onSort={sortTo}
          sortable={Boolean(onSort)}
        />
      )}
    </th>
  );
}

/**
 * Sorting state + a comparator that handles the types these tables actually
 * hold: strings (locale-aware), numbers, dates, and null/undefined.
 *
 * Nulls always sort last regardless of direction — an empty cell is not
 * "smallest", it's absent, and burying it under a descending sort would hide
 * exactly the rows a chair is usually looking for.
 */
export function useSort(defaultKey = null, defaultDirection = 'desc') {
  const [sort, setSort] = React.useState({ key: defaultKey, direction: defaultDirection });

  const toggle = React.useCallback((key) => {
    setSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  }, []);

  /** Set an explicit direction — what the header menu's sort items call. */
  const setSortTo = React.useCallback((key, direction) => setSort({ key, direction }), []);

  const compare = React.useCallback((a, b, accessor) => {
    const get = typeof accessor === 'function' ? accessor : (row) => row?.[accessor];
    const va = get(a);
    const vb = get(b);

    const aEmpty = va === null || va === undefined || va === '';
    const bEmpty = vb === null || vb === undefined || vb === '';
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;      // nulls last, both directions
    if (bEmpty) return -1;

    let result;
    if (typeof va === 'number' && typeof vb === 'number') {
      result = va - vb;
    } else if (va instanceof Date || vb instanceof Date || /^\d{4}-\d{2}-\d{2}/.test(va)) {
      result = new Date(va).getTime() - new Date(vb).getTime();
    } else {
      result = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    }
    return sort.direction === 'asc' ? result : -result;
  }, [sort.direction]);

  const sortRows = React.useCallback((rows, accessorFor) => {
    if (!sort.key || !Array.isArray(rows)) return rows;
    const accessor = accessorFor?.[sort.key] ?? sort.key;
    return [...rows].sort((a, b) => compare(a, b, accessor));
  }, [sort.key, compare]);

  return { sort, toggle, setSortTo, sortRows };
}
